import { ChangeEvent, ClipboardEvent, FocusEvent, FormEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '@indicia/component-library';
import useBem from '@indicia/use-bem';

import './numeric-input.scss';

type NumericInputProps = {
  onChange: (value: number) => void;
  value: number;
  min: number;
  max: number;
  step?: number;
  disabled?: boolean;
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void;
  instantUpdate?: boolean;
  allowDecimals?: boolean;
};

const NumericInput = ({
  onChange,
  value: propValue,
  min,
  max,
  step = 1,
  disabled = false,
  onFocus = undefined,
  instantUpdate = false,
  allowDecimals = true,
}: NumericInputProps) => {
  const [inputValue, setInputValue] = useState<string>(String(propValue ?? min));
  const { bemClassName, bemClassNames } = useBem('numeric-input');
  const { t } = useTranslation();

  const hasMoreThanOneDecimal = (value: string): boolean => {
    const decimalMatch = value.match(/[.,](\d*)/);
    return decimalMatch ? decimalMatch[1].length > 1 : false;
  };

  useEffect(() => {
    if (propValue !== undefined) {
      setInputValue(String(propValue));
    }
  }, [propValue]);

  const handleDecrement = () => {
    const currentValue = Number(inputValue);
    const hasDecimals = currentValue % 1 !== 0;

    let newValue;
    if (hasDecimals) {
      newValue = Math.floor(currentValue);
    } else {
      newValue = currentValue - step;
    }

    newValue = Math.max(newValue, min);
    setInputValue(String(newValue));
    onChange(newValue);
  };

  const handleIncrement = () => {
    const currentValue = Number(inputValue);
    const hasDecimals = currentValue % 1 !== 0;

    let newValue;
    if (hasDecimals) {
      newValue = Math.ceil(currentValue);
    } else {
      newValue = currentValue + step;
    }

    newValue = Math.min(newValue, max);
    setInputValue(String(newValue));
    onChange(newValue);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newInputValue = event.target.value;

    if (!allowDecimals && (newInputValue.includes('.') || newInputValue.includes(','))) {
      return;
    }

    if (allowDecimals && hasMoreThanOneDecimal(newInputValue)) {
      return;
    }

    setInputValue(newInputValue);

    if (instantUpdate) {
      onChange(min);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!allowDecimals && (event.key === '.' || event.key === ',' || event.key === 'Decimal')) {
      event.preventDefault();
    }
  };

  const handleBeforeInput = (event: FormEvent<HTMLInputElement>) => {
    const inputEvent = event.nativeEvent as InputEvent;
    if (!allowDecimals && inputEvent.data && (inputEvent.data === '.' || inputEvent.data === ',')) {
      event.preventDefault();
      return;
    }

    if (allowDecimals && inputEvent.data && /\d/.test(inputEvent.data)) {
      const currentValue = inputValue;
      const decimalMatch = currentValue.match(/[.,](\d*)/);
      if (decimalMatch && decimalMatch[1].length >= 1) {
        const target = event.target as HTMLInputElement;
        const selectionStart = target.selectionStart ?? 0;
        const beforeDecimal = currentValue.indexOf('.') !== -1 ? currentValue.indexOf('.') : currentValue.indexOf(',');

        if (beforeDecimal !== -1 && selectionStart > beforeDecimal && decimalMatch[1].length >= 1) {
          event.preventDefault();
        }
      }
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pastedText = event.clipboardData.getData('text');

    if (!allowDecimals && (pastedText.includes('.') || pastedText.includes(','))) {
      event.preventDefault();
      return;
    }

    if (allowDecimals && hasMoreThanOneDecimal(pastedText)) {
      event.preventDefault();
    }
  };

  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    if (onFocus) {
      onFocus(event);
    }
    requestAnimationFrame(() => {
      inputRef.current?.select();
    });
  };

  const handleBlur = () => {
    if (inputValue === '') {
      setInputValue(String(min));
      onChange(min);
      return;
    }

    const numValue = Number(inputValue);
    if (Number.isNaN(numValue)) {
      setInputValue(String(min));
      onChange(min);
      return;
    }

    let clampedValue = Math.min(Math.max(numValue, min), max);

    if (!allowDecimals) {
      clampedValue = Math.round(clampedValue);
    } else {
      clampedValue = Math.round(clampedValue * 10) / 10;
    }

    setInputValue(String(clampedValue));
    onChange(clampedValue);
  };

  return (
    <div className={bemClassName()}>
      <button
        className={bemClassNames([bemClassName('button'), bemClassName('button', 'decrement')])}
        onClick={handleDecrement}
        disabled={Number(inputValue) <= min}
        aria-label={t('generic.decrease')!}
        type="button"
      >
        <Icon
          name="minus"
          width={16}
          height={16}
        />
      </button>
      <input
        className={bemClassName('field')}
        type="number"
        inputMode="numeric"
        value={inputValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBeforeInput={handleBeforeInput}
        onPaste={handlePaste}
        onBlur={handleBlur}
        ref={inputRef}
        onFocus={handleFocus}
        min={min}
        max={max}
        step={allowDecimals ? step : 1}
        disabled={disabled}
        aria-label={t('generic.input')!}
      />
      <button
        className={bemClassNames([bemClassName('button'), bemClassName('button', 'increment')])}
        onClick={handleIncrement}
        disabled={Number(inputValue) >= max}
        aria-label={t('generic.increase')!}
        type="button"
      >
        <Icon
          name="plus"
          width={16}
          height={16}
        />
      </button>
    </div>
  );
};

export default NumericInput;
